/**
 * Customer portal support code
 *
 * current urls:
 *  - initial/passwordless login: /tools/recurring/get-subscription-access (default Recharge passwordless)
 *    no liquid file, page setup performed by js
 *
 *  - email/pass login: /account/login (default Shopify login)
 *    /templates/customers/login.liquid
 *
 *  - forgot password: /account/login (default Shopify login) - same page, switched by toggleRecoverPasswordForm in theme.js
 *    there is a separate liquid for this - not sure if it is linked from anywhere
 *
 *  - signup: /account/register (default Shopify signup)
 *    /templates/customers/register.liquid
 *
 *  - account: for now it is Recharge default - smth like https://letsliveitup.com/tools/recurring/portal/${userHash}/overview
 *      recharge/passwordless logs directly into there
 *      shopify logs into /account, and we have to redir into recharge
 */

/**
 * Thank you! If the email matches the one you used on your purchase, you'll receive a link to manage your subscription. Please check your spam folder as well! Reach out to support@artofenso.com if you have any issues getting access and we'll be happy to help you!
 */

/**
 * Phase 1: wrapping recharge pages into our wrapper
 */

window.addEventListener("DOMContentLoaded", () => {
  let portal = false;
  switch (location.pathname) {
    case "/tools/recurring/get-subscription-access":
    case "/tools/recurring/login":
      if (location.hash === "#no-redesign") break;
      new PortalPasswordlessLogin();
      portal = true;
      break;
    case "/account/login":
    case "/account/register":
      portal = true;
      break;

    case "/account":
      new PortalShopifyAccountHome();
      portal = true;
      break;
  }

  if (location.pathname.startsWith("/tools/recurring/portal")) {
    new PortalRCHelper();
  }

  if ([
    "/tools/recurring/portal",
    "/tools/recurring/pages"
  ].some(path => location.pathname.startsWith(path))) {
    addDomObserverForCancellationFlow();
  }

  if (portal) {
    document.body.classList.add("portal");
  }

  // handle the portal entrance url
  let portalLink = localStorage.getItem("portalLink");

  if (!portalLink && location.pathname.startsWith("/tools/recurring/portal")) {
    portalLink = location.href;
    localStorage.setItem("portalLink", portalLink);
  }

  if (typeof portalLink === "string") {
    document.querySelectorAll("a.portal__entrance-link").forEach(link => (link.href = portalLink));
  }
});

async function rechargeGetSession(refresh = false) {
  if (!refresh) {
    const sessionString = localStorage.getItem("rcSession");
    if (sessionString) {
      try {
        return JSON.parse(sessionString);
      } finally {
      }
    }
  }

  const session = await recharge.auth.loginShopifyAppProxy();
  if (!session) {
    console.log("loginShopifyAppProxy error");
    return null;
  }

  localStorage.setItem("rcSession", JSON.stringify(session));

  return session;
}

/**
 * serves /tools/recurring/get-subscription-access url
 */
class PortalPasswordlessLogin {
  constructor() {
    this.rcLogin = document.querySelector(".rc-login--body");
    if (!this.rcLogin) return;

    const header = this.rcLogin.querySelector(".rc-login--header-title");
    header.textContent = "Subscriber Login";

    const subheading = document.createElement("div");
    subheading.classList.add("c-rc-login-subheading");
    subheading.textContent = "Please enter the email address associated with your purchase and we'll send you a one-time code to securely access your account.";
    header.insertAdjacentElement("afterend", subheading);

    const or = document.createElement("div");
    or.innerHTML = 'Or <a href="/account/login">login with email and password</a>';
    or.classList.add("c-rc-login-bottomlink");
    this.rcLogin.appendChild(or);

    this.rcLogin.querySelector(".rc-login-form-button span").textContent = "Sign In";

    const checkSpam = document.createElement("div");
    checkSpam.textContent =
      "If the email matches the one you used on your purchase, you'll receive a one-time verification code. Please check your spam folder as well! Reach out to support@artofenso.com if you have any issues getting access and we'll be happy to help you!";
    checkSpam.classList.add("c-rc-login-checkspam");
    this.rcLogin.querySelector(".rc-enter-code").appendChild(checkSpam);

    const grid = document.createElement("div");
    grid.innerHTML = `
<div class="portal__grid"><div class="portal__grid-left"></div>
  <div class="portal__grid-right">
    <h2>New to <i>Live it Up</i>?</h2>
    <div>Don’t have an account yet?</div>
    <div class="portal__signup-link"><a href="/account/register">Sign up</a></div>
  </div>
</div>
`;
    this.rcLogin.parentNode.insertBefore(grid, this.rcLogin);
    grid.querySelector(".portal__grid-left").appendChild(this.rcLogin);
  }
}

/**
 * serves /account
 *   at Phase1, just redirects to RC Portal if available, stays here if no subscriptions available
 */
class PortalShopifyAccountHome {
  constructor() {
    if (document.referrer) {
      const u = new URL(document.referrer);
      if (u.searchParams.has("portalAction")) {
        this.action = u.searchParams.get("portalAction");

        if (this.action === "logout") {
          this.logout();
        }
      }
    }

    this.portalLink = localStorage.getItem("portalLink");
    if (!this.portalLink) {
      localStorage.setItem("portalLink", location.pathname);
    }

    this.initRCPortal();
  }

  async initRCPortal() {
    this.session = await rechargeGetSession();

    try {
      this.portalLink = await recharge.customer.getCustomerPortalAccess(this.session);
    } catch (e) {
      this.showNoRCPortal();
      console.log("Caught portal:");
      console.log(e);
    }

    this.portalLink.portal_url = this.portalLink.portal_url.replace("schedule", "overview");

    localStorage.setItem("portalLink", this.portalLink.portal_url);
    location.href = this.portalLink.portal_url;
  }

  showNoRCPortal() {
    document.querySelector(".js-loading").classList.add("hide");
    document.querySelector(".js-noPortal").classList.remove("hide");
  }

  logout() {
    location.href = "/account/logout";
  }
}

class PortalExtras {
  constructor() {
    this.main = document.getElementById("MainContent");
    if (!this.main) return;

    this.headerSection = "portal-extra-header2";

    const u = new URL(location);
    if (u.hash === "#a") {
      this.headerSection = "portal-extra-header";
    } else if (u.hash === "#b") {
      this.headerSection = "portal-extra-header2";
    } else if (u.hash === "#no-header") {
      this.headerSection = "";
    }

    this.footerSection = "portal-extra-footer";

    this.fetchExtras().then(() => this.setupPopups());
  }

  fetchExtras() {
    // setting up the placeholder for the header
    if (this.headerSection) {
      this.header = document.createElement("div");
      this.header.classList.add("portal__extra");
      this.header.innerHTML = '<div class="portal__extra-wrapper"></div>';
      this.main.insertBefore(this.header, this.main.firstChild);
    }

    let fetchUrl = "/?sections=" + this.footerSection;
    if (this.headerSection) {
      fetchUrl += "," + this.headerSection;
    }

    return fetch(fetchUrl)
      .then(response => response.json())
      .then(sections => {
        if (this.headerSection && sections[this.headerSection] === undefined) {
          console.error("Couldnt fetch portal header section " + this.headerSection);
          this.header.style.display = "none";
        } else if (this.headerSection) {
          this.setupHeader(sections[this.headerSection]);
        }

        if (sections[this.footerSection] !== undefined) {
          this.setupFooter(sections[this.footerSection]);
        }
      });
  }

  setupHeader(html) {
    this.header.outerHTML = html;

    this.header = document.getElementById("shopify-section-" + this.headerSection);
    if (!this.header) {
      console.error("Malformed fetched header");
      return;
    }

    // setting dynamic fields
    this.header.querySelectorAll("[data-user-name]").forEach(element => {
      element.textContent = window.ReCharge.customer.first_name;
    });

    // header close button
    this.header.querySelectorAll("[data-action-close]").forEach(element => {
      element.addEventListener("click", () => {
        this.header.style.display = "none";
      });
    });

    // video popup open
    this.header.querySelectorAll(".js-popup-open").forEach(element => {
      this.video = document.getElementById(element.dataset.popupId).querySelector("video");

      this.sizePopup(element.dataset.popupId);
      window.addEventListener("resize", () => this.sizePopup(element.dataset.popupId));
      this.video.addEventListener("resize", () => this.sizePopup(element.dataset.popupId));

      element.addEventListener("click", () => {
        theme.utils.showPopUp(element.dataset.popupId);
        if (this.video) {
          this.video.querySelector("source").src = this.video.querySelector("source").dataset.src;
          this.video.src = this.video.querySelector("source").dataset.src;

          this.video.play();
        }

        window._conv_q = window._conv_q || [];
        _conv_q.push(["triggerConversion", "10048668"]);

        if (window.FS) FS.event("Clicked Video in a Portal");
      });
    });

    // video popup close
    document.addEventListener("popup:closed", e => {
      if (e.detail.popUp.id === "portalVideo") e.detail.popUp.querySelector("video").pause();
    });
  }

  sizePopup(popupId) {
    const header = document.getElementById(popupId).querySelector(".i-popup-actions");
    const body = document.getElementById(popupId).querySelector(".i-popup-body");
    const video = body.querySelector("video");

    const boundingRect = {
      x: window.innerWidth * 0.9,
      y: window.innerHeight * 0.9 - header.clientHeight,
    };

    let videoAspect = 0;
    if (video.videoHeight === 0) {
      videoAspect = parseFloat(video.dataset.width) / parseFloat(video.dataset.height);
    } else {
      videoAspect = video.videoWidth / video.videoHeight;
    }
    const calculatedX = boundingRect.y * videoAspect;
    const calculatedY = boundingRect.x / videoAspect;
    const videoRect = calculatedX <= boundingRect.x ? { x: calculatedX, y: boundingRect.y } : { x: boundingRect.x, y: calculatedY };

    video.style.width = videoRect.x + "px";
    video.style.height = videoRect.y + "px";
  }

  setupFooter(html) {
    this.footer = document.createElement("div");
    this.footer.classList.add("portal__extra");
    this.main.appendChild(this.footer);
    this.footer.innerHTML = html;
  }

  setupPopups() {
    this.main.querySelectorAll(".js-popup-open").forEach(element => {
      element.addEventListener("click", () => theme.utils.showPopUp(element.dataset.popupId));
    });
  }
}

/**
 * serves changes in Recharge Portal
 *
 * for now, serves the cross-logout
 */
class PortalRCHelper {
  constructor() {
    this.rcPortal = document.getElementById("recharge-customer-portal");
    //console.log(this.rcPortal);
    this.extras = new PortalExtras();

    // handle cross-logout
    this.observer = new MutationObserver(this.observe.bind(this));
    this.observer.observe(this.rcPortal, { subtree: true, childList: true, attributes: true });

    // this.logoutButton = document.querySelector('[data-testid="recharge-internal-logout-button"]');
    // //console.log(this.logoutButton);
    // if (!this.logoutButton) return;
    // this.logoutButton.addEventListener('click', () => {
    //   document.location.hash = 'logout';
    // });

    // cancel button observer - TODO: move all portal-related stuff here instead of juggling events
    document.addEventListener("portal:cancelButtonCreated", event => {
      this.cancelButton = event.detail.cancelButton;
      this.cancelButton.addEventListener("click", () => {
        this.cancelButtonClicked();
      });
    });
  }

  cancelButtonClicked() {
    document.dispatchEvent(new CustomEvent("portal:subscriptionCancelInitiated"));

    // this.cancelObserver = new MutationObserver(this.cancelObserve.bind(this));
    // this.cancelObserver.observe(document, {subtree: true, childList: true});

    //console.log('Cancel clicked');
    // this.cancelHandler = this.buttonhandler.bind(this);
    // debugger;
  }

  cancelObserve(mutations) {
    //console.log(mutations);
    const modal = document.getElementById("PAUSE_BEFORE_CANCEL");
    if (modal) {
      //this.cancelObserver.disconnect();
    }

    if (!modal) return;

    // modal.querySelector('.recharge-button-secondary').parentNode.parentNode.addEventListener('click', e => {
    //   console.log('Real cancel!');
    // });
    const element = modal.querySelector(".recharge-button-tertiary");
    console.log(element);
    if (this.handledElement !== element) {
      console.log("new element");
      this.handledElement = element;
    } else return;

    //  console.log(element);
    //debugger;
    // console.log(element.addEventListener);

    element.firstChild.firstChild.addEventListener("click", () => {
      console.log("keep");
    });
    //element.removeEventListener('click', this.cancelHandler);
    // if (this.handledElement === undefined) {
    //   this.handledElement = element;
    // } else if (this.handledElement === element) return;

    // setTimeout(() => {
    //   element.addEventListener('click', this.cancelHandler);
    // }, 50);

    //    this.handledElement = element;

    //console.log('mutation');
    //console.log(document.getElementById('PAUSE_BEFORE_CANCEL'));
  }

  buttonhandler() {
    console.log("keep!");
  }

  observe() {
    this.logoutButton = document.querySelector('[data-testid="recharge-internal-logout-button"]');
    if (!this.logoutButton) return;

    this.observer.disconnect();

    this.logoutButton.href = "/account/logout";
    this.logoutButton.addEventListener("click", event => {
      event.cancelBubble = true;
      event.stopImmediatePropagation();
      event.stopPropagation();
      localStorage.removeItem("portalLink");
      location = "/account/logout";
      return false;
    });
  }
}

let pauseBeforeCancelListener;
const addPauseBeforeCancelListener = () => {
  const pauseBeforeCancelButton = document.querySelector('#PAUSE_BEFORE_CANCEL button.recharge-button.recharge-button-secondary');
  if (pauseBeforeCancelListener) {
    pauseBeforeCancelButton.removeEventListener("click", pauseBeforeCancelListener);
  }
  pauseBeforeCancelListener = () => {
    document.dispatchEvent(new CustomEvent("portal:cancellationFlow:dontWantToPause"));
  };
  pauseBeforeCancelButton.addEventListener("click", pauseBeforeCancelListener);
};

document.addEventListener(
  "Recharge::extension::cancellation_flow",
  (event) => {
    const { subscription } = event.detail;
    const subscriptionProductId = subscription.shopify_product_id;
    document.dispatchEvent(new CustomEvent("portal:cancellationFlow:cancellationInitiated", { detail: { subscriptionProductId } }));
    domObserver.observe(document.body, { attributes: true, childList: true, subtree: true });
  }
);

let managSubscriptionListener;
const addManageSubsriptionListener = () => {
  const manageSubscriptions = document.querySelector(
    '[data-testid="recharge-internal-general-view-button"]'
  );
  if (managSubscriptionListener) {
    manageSubscriptions.removeEventListener("click", managSubscriptionListener);
  }
  managSubscriptionListener = () => {
    document.dispatchEvent(new CustomEvent("portal:cancellationFlow:manageSubscriptionClick"));
  };
  manageSubscriptions.addEventListener("click", managSubscriptionListener);
};

let cancellationReasonConfirmedListener;
const addCancellationReasonConfirmedListener = () => {
  if (!location.pathname.startsWith("/tools/recurring/pages")) return;
  const cancellationReasonConfirmationBtn = document.querySelector('#recharge-onsite .recharge-card form .recharge-button.recharge-button-primary')
  if (!cancellationReasonConfirmationBtn) return;
  if (cancellationReasonConfirmedListener) {
    cancellationReasonConfirmationBtn.removeEventListener("click", cancellationReasonConfirmedListener);
  }
  cancellationReasonConfirmedListener = () => {
    document.dispatchEvent(new CustomEvent("portal:cancellationFlow:cancellationReasonConfirmed"));
  };
  cancellationReasonConfirmationBtn.addEventListener("click", cancellationReasonConfirmedListener);
};

const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  }
}
const debouncedCustomerFeedbackConfirmed = debounce(() => {
  document.dispatchEvent(new CustomEvent("portal:cancellationFlow:customerFeedbackConfirmed"));
}, 200);
const confirmFeedbackButtonObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.target?.querySelector('[aria-live="polite"][aria-busy="true"][role="progressbar"]')) {
      debouncedCustomerFeedbackConfirmed();
    }
  });
});
const addCustomerFeedbackConfirmedListener = () => {
  if (!location.pathname.startsWith("/tools/recurring/pages")) return;
  const customerFeedbackConfirmationBtn = document.querySelector('[aria-label="Offers"] .recharge-card form .recharge-button.recharge-button-primary');
  if (!customerFeedbackConfirmationBtn) return;
  let confirmButtonObserverElement = document.querySelector('[aria-label="Offers"] .recharge-card form .recharge-button.recharge-button-primary');
  if (confirmButtonObserverElement) {
    confirmFeedbackButtonObserver.disconnect();
    confirmFeedbackButtonObserver.observe(confirmButtonObserverElement, { attributes: true, childList: true, subtree: true });
  }
};

const domObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    const target = mutation.target;
    if (target.querySelector('[data-testid="recharge-internal-general-view-button"]')) {
      addManageSubsriptionListener();
    }
    if (target.querySelector('#PAUSE_BEFORE_CANCEL button.recharge-button.recharge-button-secondary')) {
      addPauseBeforeCancelListener();
    }
    if (target.querySelector('#recharge-onsite .recharge-card form .recharge-button.recharge-button-primary')) {
      addCancellationReasonConfirmedListener();
    }
    if (target.querySelector('[aria-label="Offers"] .recharge-card form .recharge-button.recharge-button-primary')) {
      addCustomerFeedbackConfirmedListener();
    }
  });
});

const addDomObserverForCancellationFlow = () => {
  const elementObserved = document?.body;
  if (!elementObserved) return;
  domObserver.observe(elementObserved, { attributes: true, childList: true, subtree: true });
};
